// Buzones operativos de Atención Ciudadana.
// Barra de dependencias (buzones) → lista de tickets → detalle con la
// conversación del ciudadano, la evidencia y el control de estado.
// Backend: /api/v1/admin/citizen-reports/* (filtra por org_id del JWT).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronRight, Inbox, MapPin, RefreshCw } from 'lucide-react';
import { listBuzones, listReports } from '../api';
import type { Buzon, CitizenReport } from '../types';
import { OsApp, osActionBtn } from '@/modules/orion-shell/OsApp';
import { Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { CitizenTabs } from './CitizenTabs';
import { TicketDetail } from './TicketDetail';
import { FilterChip, NoteChip, PRIORITY_META, StatusChip } from './parts';

// Filtros de estado del tablero de tickets. '' = abiertos (aún sin atender).
const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'Abiertos' },
  { key: 'recibido', label: 'Recibido' },
  { key: 'asignado', label: 'Asignado' },
  { key: 'en_proceso', label: 'En proceso' },
  { key: 'atendido', label: 'Atendido' },
  { key: 'cerrado', label: 'Cerrado' },
  { key: 'all', label: 'Todos' },
];
const OPEN_STATES = ['recibido', 'asignado', 'en_proceso'];

// "Hace cuánto" entró el ticket — para priorizar por tiempo de espera.
function daysWaiting(createdAt: string): number {
  if (!createdAt) return 0;
  const ms = Date.now() - new Date(createdAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}
function waitingLabel(d: number): string {
  if (d <= 0) return 'hoy';
  if (d === 1) return 'hace 1 día';
  return `hace ${d} días`;
}

// Las pestañas por RESPONSABLE se generan DINÁMICAMENTE desde el backend
// (GET /buzones, org-scoped). El frontend solo renderiza lo que recibe: si un
// municipio configura otras dependencias, sus pestañas salen solas.
export function CitizenInbox() {
  const [buzones, setBuzones] = useState<Buzon[]>([]);
  const [selected, setSelected] = useState<string>(''); // responsable activo
  const [statusFilter, setStatusFilter] = useState<string>(''); // '' = abiertos
  const [reports, setReports] = useState<CitizenReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<CitizenReport | null>(null);

  const loadBuzones = () => {
    setLoading(true);
    listBuzones()
      .then((b) => {
        setBuzones(b);
        setError(null);
        setSelected((cur) => cur || (b[0]?.dependency ?? ''));
      })
      .catch((e) => setError(e?.message ?? 'Error cargando responsables'))
      .finally(() => setLoading(false));
  };
  useEffect(loadBuzones, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReports = (dep: string) => {
    if (!dep) return;
    listReports({ dependency: dep, limit: 200 })
      .then(setReports)
      .catch((e) => setError(e?.message ?? 'Error cargando tickets'));
  };
  useEffect(() => { loadReports(selected); }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtra por estado y ORDENA por mayor tiempo de espera (el más viejo arriba):
  // los tickets que llevan más tiempo sin atenderse encabezan la lista.
  const visibleReports = useMemo(() => {
    let rs = reports;
    if (statusFilter === '') rs = rs.filter((r) => OPEN_STATES.includes(r.status));
    else if (statusFilter !== 'all') rs = rs.filter((r) => r.status === statusFilter);
    return [...rs].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [reports, statusFilter]);

  const refresh = () => {
    loadBuzones();
    loadReports(selected);
  };

  const frame = (content: ReactNode) => (
    <OsApp
      icon={Inbox}
      title="Tickets por responsable"
      tabs={<CitizenTabs value="buzones" />}
      subtitle="Bandeja por dependencia · lo que espera atención primero"
      actions={
        <button type="button" onClick={refresh} className={osActionBtn}>
          <RefreshCw className="h-4 w-4" aria-hidden /> Actualizar
        </button>
      }
    >
      {content}
    </OsApp>
  );

  if (loading && !buzones.length) return frame(<Loading label="Cargando responsables…" />);
  if (error && !buzones.length) return frame(<ErrorNote>{error}</ErrorNote>);
  if (!buzones.length) {
    return frame(
      <EmptyState icon={<Inbox size={18} />} title="Aún no hay buzones">
        Los buzones salen de la matriz de dependencias de la organización; en cuanto entre el primer reporte aparecen aquí.
      </EmptyState>,
    );
  }

  const actual = buzones.find((b) => b.dependency === selected);

  return frame(
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
      {/* Escritorio: carpetas por dependencia (como la bandeja de Leads). Dinámicas
          desde el backend: si un municipio configura otras dependencias, salen solas. */}
      <nav aria-label="Dependencias" className="hidden lg:block">
        <Card bodyClassName="!p-2">
          <ul className="flex flex-col gap-0.5">
            {buzones.map((b) => {
              const on = selected === b.dependency;
              return (
                <li key={b.dependency}>
                  <button
                    type="button"
                    onClick={() => setSelected(b.dependency)}
                    aria-current={on ? 'true' : undefined}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      on ? 'bg-primary/10 font-semibold text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                    title={`${b.abiertos} abiertos · ${b.total} en total`}
                  >
                    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${on ? 'bg-primary/15 text-primary' : 'bg-muted'}`}>
                      <Inbox className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{b.dependency}</span>
                    <span className="tabular-nums text-xs">{b.abiertos}</span>
                    {b.urgentes > 0 && (
                      <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold text-red-700 dark:text-red-300" aria-label={`${b.urgentes} urgentes`}>
                        {b.urgentes}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      </nav>

      <div className="flex min-w-0 flex-col gap-3">
        {/* Móvil / tableta: dependencias como pastillas desplazables. */}
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 lg:hidden" role="group" aria-label="Dependencias">
          {buzones.map((b) => (
            <FilterChip key={b.dependency} active={selected === b.dependency} onClick={() => setSelected(b.dependency)}
              title={`${b.abiertos} abiertos · ${b.total} en total`}>
              {b.dependency}
              <span className="tabular-nums opacity-70">{b.abiertos}</span>
              {b.urgentes > 0 && (
                <span className="rounded-full bg-red-500/15 px-1.5 text-[10px] font-bold text-red-700 dark:text-red-300">{b.urgentes}</span>
              )}
            </FilterChip>
          ))}
        </div>

        <Card
          title={<span>{selected || 'Selecciona una dependencia'} <span className="font-normal text-muted-foreground">· {visibleReports.length} tickets</span></span>}
          action={<span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">Primero lo que lleva más tiempo esperando</span>}
          bodyClassName="flex flex-col gap-3"
        >
          {actual && (
            <p className="-mt-1 text-xs text-muted-foreground">
              {actual.abiertos} abiertos · {actual.total} en total{actual.urgentes > 0 ? ` · ${actual.urgentes} urgentes` : ''}
            </p>
          )}
          {/* Filtro por estado */}
          <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 sm:flex-wrap" role="group" aria-label="Filtrar por estado">
            {STATUS_FILTERS.map((f) => (
              <FilterChip key={f.key || 'open'} active={statusFilter === f.key} onClick={() => setStatusFilter(f.key)}>
                {f.label}
              </FilterChip>
            ))}
          </div>
          {visibleReports.length === 0 ? (
            <EmptyState icon={<Inbox size={18} />} title="Sin tickets con este filtro">
              Prueba «Todos» o elige otra dependencia.
            </EmptyState>
          ) : (
            <ul className="flex max-h-[68vh] flex-col gap-1.5 overflow-y-auto pr-1">
              {visibleReports.map((r) => {
                const d = daysWaiting(r.created_at);
                const resolved = ['atendido', 'cerrado'].includes(r.status);
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setActive(r)}
                      className="flex w-full items-start gap-3 rounded-xl border border-border px-3 py-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/40"
                    >
                      <span
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: PRIORITY_META[r.priority] ?? '#64748b' }}
                        title={`Prioridad: ${r.priority}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="font-mono text-xs text-muted-foreground">{r.folio}</span>
                          <StatusChip status={r.status} />
                          {r.is_guide && <NoteChip tone="muted">guía</NoteChip>}
                          {r.in_my_zone && (
                            <NoteChip icon={MapPin} title={`En tu zona · atiende ${r.dependency}`}>en tu zona · {r.dependency}</NoteChip>
                          )}
                          {/* Tiempo de espera — rojo si lleva mucho sin atenderse */}
                          <span className={`ml-auto shrink-0 text-xs ${
                            !resolved && d >= 7 ? 'font-semibold text-red-700 dark:text-red-300' : 'text-muted-foreground'}`}>
                            {waitingLabel(d)}
                          </span>
                        </span>
                        <span className="mt-1 block truncate text-sm font-medium text-foreground">
                          {r.category_label}<span className="font-normal text-muted-foreground"> · {r.description}</span>
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{r.address}</span>
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {active && (
        <TicketDetail
          report={active}
          onClose={() => setActive(null)}
          onChanged={(updated) => {
            setActive(updated);
            setReports((rs) => rs.map((x) => (x.id === updated.id ? updated : x)));
            loadBuzones();
          }}
        />
      )}
    </div>,
  );
}
